/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* global document window navigator XMLHttpRequest */

/**
 * @module JSG
 * @namespace JSG
 */
import {
	default as JSG,
	Notification,
	NotificationCenter,
	MetricCoordinateSystem,
	ImagePool,
	Point
} from '@cedalo/jsg-core';
import { Locale } from '@cedalo/parser';
import '../res/css/jsg.css';

import Tooltip from './graph/interaction/Tooltip';
import ScalableGraphics from './ui/graphics/ScalableGraphics';
import Cursor from './ui/Cursor';

import CursorCross from '../res/cross.png';
import CursorRotate from '../res/rotate.png';
import CursorSheet from '../res/sheet.png';
import CursorSheetrow from '../res/sheetrow.png';
import CursorSheetcolumn from '../res/sheetcolumn.png';
import CursorSheetrowsize from '../res/rowsize.png';
import CursorSheetrowsizedisabled from '../res/rowsizedisabled.png';
import CursorSheetcolumnsize from '../res/columnsize.png';
import CursorDeny from '../res/deny.png';
import CursorSplitv from '../res/splitv.png';
import CursorSplith from '../res/splith.png';

import IECursorCross from '../res/cross.cur';
import IECursorRotate from '../res/rotate.cur';
import IECursorSheet from '../res/sheet.cur';
import IECursorSheetrow from '../res/sheetrow.cur';
import IECursorSheetcolumn from '../res/sheetcolumn.cur';
import IECursorSheetrowsize from '../res/rowsize.cur';
import IECursorSheetrowsizedisabled from '../res/rowsizedisabled.cur';
import IECursorSheetcolumnsize from '../res/columnsize.cur';
import IECursorDeny from '../res/deny.cur';
import IECursorSplitv from '../res/splitv.cur';
import IECursorSplith from '../res/splith.cur';

import IconNotavailable from '../res/images/notavailable.png';
import IconExpanded from '../res/images/expanded.png';
import IconCollapsed from '../res/images/collapsed.png';
import IconBold from '../res/images/bold.png';
import IconItalic from '../res/images/italic.png';
import IconUnderline from '../res/images/underline.png';
import IconLeftalign from '../res/images/leftalign.png';
import IconCenteralign from '../res/images/centeralign.png';
import IconRightalign from '../res/images/rightalign.png';
import IconFontcolor from '../res/images/fontcolor.png';
import IconFontsizeup from '../res/images/fontsizeup.png';
import IconFontsizedown from '../res/images/fontsizedown.png';
import IconBullets from '../res/images/bullets.png';
import IconNumbered from '../res/images/numbered.png';
import IconTreechecked from '../res/images/treechecked.png';
import IconTreeunchecked from '../res/images/treeunchecked.png';
import IconDelete from '../res/svg/delete.svg';
import IconEdge from '../res/svg/edge.svg';
import IconHvedge from '../res/svg/hvedge.svg';
import IconArrowup from '../res/svg/arrowup.svg';
import IconArrowdown from '../res/svg/arrowdown.svg';
import IconFilter from '../res/svg/filter.svg';
import IconTable from '../res/svg/table.svg';
import IconChecked from '../res/svg/checked.svg';
import IconUnchecked from '../res/svg/unchecked.svg';

const IconArrowleft = IconArrowdown
const IconArrowright = IconArrowdown

JSG.touchDevice = !!('ontouchstart' in window) || !!('msmaxtouchpoints' in window.navigator);

// Browser detection....
JSG.isFF = typeof InstallTrigger !== 'undefined';
const isIE = false || !!document.documentMode;
// Edge 20+
const isEdge = !isIE && !!window.StyleMedia;

JSG.browserVersion = () => {
	const ua = navigator.userAgent;
	let tem;
	// eslint-disable-next-line
	let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*([\d\.]+)/i) || [];

	if (/trident/i.test(M[1])) {
		tem = /\brv[ :]+(\d+(\.\d+)?)/g.exec(ua) || [];
		return `IE ${tem[1] || ''}`;
	}
	M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
	// eslint-disable-next-line
	if ((tem = ua.match(/version\/([\.\d]+)/i)) !== null) {
		M[2] = tem[1];
	}
	return M;
};
JSG.isSafari = JSG.browserVersion()[0] && JSG.browserVersion()[0].toLowerCase() === 'safari';
JSG.isMobileSafari = JSG.touchDevice && JSG.isSafari;

JSG.canvasSupported = !!window.HTMLCanvasElement;

JSG.bkColorHeader = '#F3F3F3';
JSG.bkColorScroll = '#F3F3F3';
JSG.bkColorButton = '#CCCCCC';

JSG.findRadius = JSG.touchDevice ? 600 : 300;
JSG.scaledFindRadius = JSG.touchDevice ? 600 : 300;
JSG.portFindRadius = JSG.touchDevice ? 400 : 200;
JSG.createThreshhold = 100;
JSG.snapRadius = 300;
JSG.keepFocus = false;
JSG.canvasCache = true;
JSG.feedbackOptions = {
	position: true,
	angle: true,
	size: true,
	edges: true
};
JSG.zoomMarkChanged = true;

JSG.isMacOS =
	navigator.userAgent.indexOf('Mac') > -1 ||
	navigator.platform.indexOf('Mac') > -1 ||
	navigator.appVersion.indexOf('Mac') > -1;
// navigator.userAgentData.platform === 'macOS'

JSG.colors = [
	'FFFFFF',
	'000000',
	'222222',
	'444444',
	'666666',
	'888888',
	'AAAAAA',
	'CCCCCC',
	'EEEEEE',
	'660000',
	'663300',
	'996633',
	'003300',
	'003333',
	'003399',
	'000066',
	'330066',
	'660066',
	'990000',
	'993300',
	'CC9900',
	'006600',
	'336666',
	'0033FF',
	'000099',
	'660099',
	'990066',
	'CC0000',
	'CC3300',
	'FFCC00',
	'009900',
	'006666',
	'0066FF',
	'0000CC',
	'663399',
	'CC0099',
	'FF0000',
	'FF3300',
	'FFFF00',
	'00CC00',
	'009999',
	'0099FF',
	'0000FF',
	'9900CC',
	'FF0099',
	'CC3333',
	'FF6600',
	'FFFF33',
	'00FF00',
	'00CCCC',
	'00CCFF',
	'3366FF',
	'9933FF',
	'FF00FF',
	'FF6666',
	'FF6633',
	'FFFF66',
	'66FF66',
	'66CCCC',
	'00FFFF',
	'3399FF',
	'9966FF',
	'FF66FF',
	'FF9999',
	'FF9966',
	'FFFF99',
	'99FF99',
	'66FFCC',
	'99FFFF',
	'66CCFF',
	'9999FF',
	'FF99FF',
	'FFCCCC',
	'FFCC99',
	'FFFFCC',
	'CCFFCC',
	'99FFCC',
	'CCFFFF',
	'99CCFF',
	'CCCCFF',
	'FFCCFF'
];

JSG.toolTip = new Tooltip();

/**
 * Factory to provide and register different {{#crossLink "Layout"}}{{/crossLink}}s.
 *
 * @property layoutFactory
 * @type {LayoutFactory}
 * @static
 * @since 1.6.18
 */
// JSG.layoutFactory = undefined;

JSG.defaultEdgeType = 'edge';

/**
 * Property used to measure text.<br/>
 * <b>Note:</b> before using this property the library must be have been initialized by calling
 * {{#crossLink "JSG/init:method"}}{{/crossLink}}.
 *
 * @property graphics
 * @type {ScalableGraphics}
 * @static
 */
JSG.graphics = undefined;

/**
 * To turn drawing off or on. Useful to prevent too many drawing operations. Use with care.
 * @property drawingDisabled
 * @type {Boolean}
 * @static
 */
JSG.drawingDisabled = false;

/**
 * Notification flag. This notification is send via NotificationCenter whenever the drawing is
 * globally enabled or disabled.
 *
 * See {{#crossLink "NotificationCenter"}}{{/crossLink}}.
 * @property DRAW_DISABLED_NOTIFICATION
 * @type String
 * @static
 */
JSG.COMMAND_EXECUTED_NOTIFICATION = 'jsg.command.executed.notification';
JSG.DRAW_DISABLED_NOTIFICATION = 'jsg.draw.disabled.notification';
JSG.LOCALE_CHANGED_NOTIFICATION = 'jsg.locale.changed.notification';
JSG.GRAPH_SHOW_CONTEXT_MENU_NOTIFICATION = 'graph_ctx_notification';
JSG.EDITPOINTS_SHOW_CONTEXT_MENU_NOTIFICATION = 'points_ctx_notification';
JSG.GRAPH_INVALID_NOTIFICATION = 'graph_invalid_notification';
JSG.SHOW_SHAPE_SETTINGS = 'shape_dbl_clk';
/**
 * Localized strings.
 * @property _localizedStrings
 * @type Object
 * @private
 * @static
 */
JSG._localizedStrings = {};

JSG.setDrawingDisabled = (status) => {
	if (status !== JSG.drawingDisabled) {
		JSG.drawingDisabled = status;
		NotificationCenter.getInstance().send(new Notification(JSG.DRAW_DISABLED_NOTIFICATION));
	}
};

JSG.copyItems = (selection) => {
	const file = new JSG.JSONWriter();
	const items = selection.length === undefined ? [selection] : selection;
	const graphItems = items[0].getModel().getParent().subItems;
	let valid = false;

	// sort items first, so drawing order of copied items does not change
	items.sort((a, b) => {
		const ia = graphItems.indexOf(a.getModel());
		const ib = graphItems.indexOf(b.getModel());
		return ia < ib ? -1 : 1;
	});

	file.writeStartDocument();
	file.writeStartElement('clip');
	file.writeStartArray('graphitem');

	items.forEach((sel) => {
		const restriction = sel.getModel().getCopyRestriction();
		switch (restriction) {
			case 'prohibit':
				break;
			case 'children':
				sel.getModel().subItems.forEach((item) => {
					item.resolveParentReferences(true);
					item.save(file, true);
					valid = true;
				});
				break;
			case 'none':
			default:
				sel.getModel().resolveParentReferences(true);
				sel.getModel().save(file, true);
				valid = true;
				break;
		}
	});

	file.writeEndArray('graphitem');
	file.writeEndElement();

	// save all data uri images to restore them
	file.writeStartElement('images');

	items.forEach((item) => {
		const pattern = item.getModel().getFormat().getPattern().getValue();
		if (pattern.indexOf('dataimage') !== -1) {
			file.writeStartElement(pattern);
			const image = JSG.imagePool.get(pattern);
			if (image !== undefined) {
				file.writeStartElement('data');
				file.writeString(image.src);
				file.writeEndElement();
			}
			file.writeEndElement();
		}
	});

	file.writeEndElement();
	file.writeEndDocument();

	// this.debug.log(file.flush());

	return valid ? file.flush() : undefined;
};

/**
 * Initialize the library. Must be called before first usage. <br/>
 * As optional parameter a locale string can be specified to load string localizations from the resource directory
 * <code>res/i18n</code>. The expected locale string is simply the file name without its extension. E.g. to load the
 * german localization simply pass <code>de-de</code> as second parameter.
 *
 * @method init
 * @param {String} jsghome A string which reference the directory where jsg.js is located and loaded from
 * @param {String} [locale] An optional locale string to load different localizations.
 * @static
 */
JSG.init = (jsghome, locale) => {
	JSG.home = jsghome;
	JSG.locale = locale;

	// init some static properties:
	JSG.dpi = (() => {
		const tmpDiv = document.createElement('div');
		tmpDiv.setAttribute('id', 'TestDiv');
		tmpDiv.setAttribute('style', 'height: 1in; left: -100%; position: absolute; top: -100%; width: 1in;');
		document.body.appendChild(tmpDiv);
		const dpi = new Point(tmpDiv.offsetWidth, tmpDiv.offsetHeight);
		document.body.removeChild(tmpDiv);
		return dpi;
	})();
	JSG.graphics = new ScalableGraphics(document.createElement('canvas'), new MetricCoordinateSystem());

	// JSG images => TODO we need something better...
	JSG.imagePool.add(IconNotavailable, ImagePool.IMG_NOTAVAIL);
	JSG.imagePool.add(IconExpanded, ImagePool.IMG_EXPANDED);
	JSG.imagePool.add(IconCollapsed, ImagePool.IMG_COLLAPSED);

	JSG.imagePool.add(IconBold, ImagePool.IMG_BOLD);
	JSG.imagePool.add(IconItalic, ImagePool.IMG_ITALIC);
	JSG.imagePool.add(IconUnderline, ImagePool.IMG_UNDERLINE);

	JSG.imagePool.add(IconLeftalign, ImagePool.IMG_LEFT_ALIGN);
	JSG.imagePool.add(IconCenteralign, ImagePool.IMG_CENTER_ALIGN);
	JSG.imagePool.add(IconRightalign, ImagePool.IMG_RIGHT_ALIGN);

	JSG.imagePool.add(IconFontcolor, ImagePool.IMG_FONTCOLOR);
	JSG.imagePool.add(IconFontsizeup, ImagePool.IMG_FONTSIZE_UP);
	JSG.imagePool.add(IconFontsizedown, ImagePool.IMG_FONTSIZE_DOWN);

	JSG.imagePool.add(IconBullets, ImagePool.IMG_BULLETS);
	JSG.imagePool.add(IconNumbered, ImagePool.IMG_NUMBERED);

	JSG.imagePool.add(IconTreechecked, ImagePool.IMG_TREE_CHECKED);
	JSG.imagePool.add(IconTreeunchecked, ImagePool.IMG_TREE_UNCHECKED);

	// load SVGs
	// JSG.imagePool.add(jsghome + "/res/svg/delete_24px.svg", 'md_delete_forever');
	JSG.imagePool.add(IconDelete, ImagePool.SVG_DELETE);
	JSG.imagePool.add(IconEdge, ImagePool.SVG_CREATE_EDGE);
	JSG.imagePool.add(IconHvedge, ImagePool.SVG_CREATE_HVEDGE);
	JSG.imagePool.add(IconArrowup, ImagePool.SVG_MOVE_UP);
	JSG.imagePool.add(IconArrowdown, ImagePool.SVG_MOVE_DOWN);
	JSG.imagePool.add(IconArrowleft, ImagePool.SVG_MOVE_LEFT);
	JSG.imagePool.add(IconArrowright, ImagePool.SVG_MOVE_RIGHT);
	JSG.imagePool.add(IconFilter, ImagePool.SVG_FILTER);
	JSG.imagePool.add(IconTable, ImagePool.SVG_TABLE);
	JSG.imagePool.add(IconChecked, ImagePool.SVG_CHECKED);
	JSG.imagePool.add(IconUnchecked, ImagePool.SVG_UNCHECKED);

	if (isIE || isEdge) {
		Cursor.Style.CROSS = `url(${IECursorCross}), crosshair`;
		Cursor.Style.ROTATE = `url(${IECursorRotate}), move`;
		Cursor.Style.SHEET = `url(${IECursorSheet}), move`;
		Cursor.Style.SHEETROW = `url(${IECursorSheetrow}), move`;
		Cursor.Style.SHEETCOLUMN = `url(${IECursorSheetcolumn}), move`;
		Cursor.Style.SHEETROWSIZE = `url(${IECursorSheetrowsize}), move`;
		Cursor.Style.SHEETROWSIZEDISABLED = `url(${IECursorSheetrowsizedisabled}), move`;
		Cursor.Style.SHEETCOLUMNSIZE = `url(${IECursorSheetcolumnsize}), move`;
		Cursor.Style.DENY = `url(${IECursorDeny}), auto`;
		Cursor.Style.SPLITV = `url(${IECursorSplitv}), move`;
		Cursor.Style.SPLITH = `url(${IECursorSplith}), move`;
		Cursor.Style.FORMATPAINT = 'copy'; // TODO "url(" + jsghome + "/res/formatpainter.cur), copy";
	} else {
		Cursor.Style.CROSS = `url(${CursorCross}) 15 15, crosshair`;
		Cursor.Style.ROTATE = `url(${CursorRotate}) 15 15, move`;
		Cursor.Style.SHEET = `url(${CursorSheet}) 15 15, move`;
		Cursor.Style.SHEETROW = `url(${CursorSheetrow}) 15 15, move`;
		Cursor.Style.SHEETCOLUMN = `url(${CursorSheetcolumn}) 15 15, move`;
		Cursor.Style.SHEETROWSIZE = `url(${CursorSheetrowsize}) 15 15, move`;
		Cursor.Style.SHEETROWSIZEDISABLED = `url(${CursorSheetrowsizedisabled}) 15 15, move`;
		Cursor.Style.SHEETCOLUMNSIZE = `url(${CursorSheetcolumnsize}) 15 15, move`;
		Cursor.Style.DENY = `url(${CursorDeny}) 15 15, auto`;
		Cursor.Style.SPLITV = `url(${CursorSplitv}) 15 15, move`;
		Cursor.Style.SPLITH = `url(${CursorSplith}) 15 15, move`;
		Cursor.Style.FORMATPAINT = 'copy'; // TODO "url(" + jsghome + "/res/formatpainter.png) 15 15, copy";
	}
	// JSG.layoutFactory = LayoutFactory;

	JSG.setLocale(locale);
	// load our css...
	// JSG.loadCSS(`${jsghome}/res/css/jsg.css`);
};

// TODO: review
JSG.appLocalizer = undefined;

JSG.setParserLocale = (locale) => {
	JSG.parserLocale = locale;
};

JSG.getParserLocale = () => (JSG.parserLocale ? JSG.parserLocale : 'en');

JSG.getParserLocaleSettings = () => {
	switch (JSG.parserLocale) {
		case 'de':
			return Locale.DE;
		case 'en':
		default:
			return Locale.EN;
	}
};

JSG.setLocale = (locale, finish) => {
	if (locale) {
		const xhr = JSG._createRequest(`${JSG.home}/res/i18n/${locale.toLowerCase()}.json`, {
			onload(response) {
				JSG._localizedStrings = JSON.parse(response);
				NotificationCenter.getInstance().send(new Notification(JSG.LOCALE_CHANGED_NOTIFICATION, this));
				if (finish) {
					finish();
				}
			},
			onerror() {
				JSG.debug.logError(`Failed to load ${locale}.json`);
			}
		});
		xhr.send();
	}
	JSG.locale = locale;
};

/**
 * Helper method to init specified icon fonts.
 * Can probably fix drawing problem on first usage of given fonts.
 * @method initIconFont
 * @param {String*} names A comma separated list of font family names.
 */
JSG.initIconFont = (...args) => {
	const ctxt = JSG.graphics && JSG.graphics.getContext();
	if (ctxt) {
		let i;
		const n = args.length;

		for (i = 0; i < n; i += 1) {
			// console.log("init "+name);
			ctxt.font = `0pt ${args[i]}`;
			ctxt.fillText('\u0000', 0, 0);
		}
	}
};

/**
 * Creates a new XMLHttpRequest for given url.<br/>
 * Note: this is currently API internal...
 *
 * @method _createRequest
 * @param {String} url The URL to request
 * @param {String} [callback] An optional callback object <code>{onload:Function, onerror:Function}</code>
 * @return {XMLHttpRequest} A new XMLHttpRequest object
 * @private
 */
JSG._createRequest = (url, callback) => {
	const xhr = new XMLHttpRequest();
	xhr.open('GET', url, true);
	xhr.onload = (ev) => {
		if (callback && callback.onload) {
			callback.onload.call(callback, xhr.response);
		}
	};
	xhr.onerror = (ev) => {
		if (callback && callback.onerror) {
			callback.onerror.call(callback);
		}
	};
	xhr.onabort = (ev) => {
		if (callback && callback.onerror) {
			callback.onerror.call(callback);
		}
	};
	return xhr;
};
/**
 * Currently <b>API INTERNAL!!</b>
 * @method loadCSS
 * @param {String} url The url of the CSS file to load. Should be relative to <code>JSG.home</code>.
 * @param {Function} callback Optional function which gets called if CSS file was loaded or loading failed...
 */
JSG.loadCSS = (url, callback) => {
	function notifyCallback(err) {
		if (callback) {
			callback(err);
		}
	}

	const link = document.createElement('link');
	link.href = url;
	link.rel = 'stylesheet';
	link.type = 'text/css';
	if (link.readyState) {
		// IE
		link.onreadystatechange = () => {
			if (link.readyState === 'complete' || link.readyState === 'loaded') {
				link.onreadystatechange = null;
				notifyCallback();
			}
		};
	} else {
		// Others
		link.onload = () => {
			notifyCallback();
		};
		link.onerror = (ev) => {
			notifyCallback({ cause: 'failed', url, event: ev });
		};
	}
	document.head.appendChild(link);
};
/**
 * Currently <b>API INTERNAL!!</b>
 * @method isStyleAvailable
 * @param {String} selector A CSS style selector string, e.g.: <code>".my-css-selector"</code>.
 */
JSG.isStyleAvailable = (selector) => {
	const stylelist = document.styleSheets;
	return [].slice.call(stylelist).some((styleSheet) => {
		if (styleSheet.cssRules) {
			return [].slice.call(styleSheet.cssRules).some((cssRule) => cssRule.selectorText.indexOf(selector) > -1);
		}
		return undefined;
	});
};
/**
 * Returns localized version of given string or the string itself if no localizations exists.<br/>
 * The translated strings are read from JSON files under <code>/res/i18n/</code>.
 * @method getLocalizedString
 * @param {String} str The string to localize.
 * @return {String} The localized string.
 * @since 1.6.43
 */
JSG.getLocalizedString = (str) => JSG._localizedStrings[str] || str;
JSG.streamSheets = [];
JSG.findSheetModel = (name) => {
	const model = JSG.streamSheets.find((streamSheet) => streamSheet.name.toUpperCase() === name.toUpperCase());
	return model ? model.sheet : undefined;
};

export default JSG;
